import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";

import { useHasRole } from "../../../hooks/useHasRole";
import {
  getPreferences,
  messageForError,
  stateForError,
  updatePreferences,
} from "../../../services/notificationSettings";
import {
  PanelState,
  PreferenceUpdate,
  UserNotificationPreferences,
} from "../../../types/notificationSettings";
import { formatInstant } from "../../../utils/instantFormat";

const CATEGORIES: { key: "owner_alerts" | "subscribed"; label: string }[] = [
  { key: "owner_alerts", label: "Owner alerts" },
  { key: "subscribed", label: "Subscribed stations" },
];

const NotificationPreferencesPanel = ({ userId }: { userId: string }) => {
  const canEdit = useHasRole("admin");
  const [state, setState] = useState<PanelState>("loading");
  const [prefs, setPrefs] = useState<UserNotificationPreferences | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const [snoozeAt, setSnoozeAt] = useState<Date | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      setPrefs(await getPreferences(userId));
      setState("ready");
    } catch (e) {
      setError(messageForError(e));
      setState(stateForError(e));
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Sends one change and renders whatever the service returns, never the clicked value. */
  const apply = async (update: PreferenceUpdate) => {
    setBusy(true);
    setWriteError(null);
    try {
      setPrefs(await updatePreferences(userId, update));
    } catch (e) {
      setWriteError(messageForError(e));
    } finally {
      setBusy(false);
    }
  };

  if (state === "loading") {
    return (
      <div className="flex align-items-center gap-3 py-4" data-testid="preferences-loading">
        <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
        <span className="text-color-secondary">Loading preferences…</span>
      </div>
    );
  }

  if (state === "refused") {
    return (
      <div className="py-3" data-testid="preferences-refused">
        <Message severity="warn" text="Viewing another person's preferences requires an administrator entitlement." />
      </div>
    );
  }

  if (state === "failed") {
    return (
      <div className="py-3" data-testid="preferences-failed">
        <Message severity="error" text={`Could not load preferences${error ? `: ${error}` : ""}`} />
      </div>
    );
  }

  const snoozedUntil = prefs?.ownerAlerts.snoozedUntil ?? null;

  return (
    <div data-testid="preferences-ready" className="flex flex-column gap-4">
      <div data-testid={prefs?.email ? "email-on-file" : "email-missing"}>
        <span className="text-color-secondary text-sm">Alerts are emailed to </span>
        {prefs?.email ? (
          <span className="font-medium">{prefs.email}</span>
        ) : (
          <Tag severity="warning" value="no address on file" />
        )}
        <div className="text-xs text-color-secondary mt-1">
          Set when the person subscribes in the app; it cannot be changed from here.
        </div>
      </div>

      {CATEGORIES.map(({ key, label }) => {
        const category = key === "owner_alerts" ? prefs?.ownerAlerts : prefs?.subscribed;
        return (
          <div key={key} className="flex flex-column gap-2">
            <div className="font-medium">{label}</div>
            {Object.entries(category?.channels ?? {}).map(([channel, on]) => (
              <div key={channel} className="flex align-items-center gap-3">
                {canEdit ? (
                  <InputSwitch
                    data-testid={`switch-${key}-${channel}`}
                    checked={on}
                    disabled={busy}
                    onChange={(e) => apply({ category: key, channels: { [channel]: e.value } })}
                  />
                ) : (
                  <Tag
                    data-testid={`readonly-${key}-${channel}`}
                    severity={on ? "success" : "danger"}
                    value={on ? "on" : "off"}
                  />
                )}
                <span>{channel}</span>
              </div>
            ))}
          </div>
        );
      })}

      <div className="flex flex-column gap-2">
        <div className="font-medium">Snooze owner alerts</div>
        {snoozedUntil ? (
          <div className="flex align-items-center gap-3" data-testid="snooze-active">
            <Tag severity="warning" value={`snoozed until ${formatInstant(snoozedUntil)}`} />
            {canEdit && (
              <Button
                label="Clear snooze"
                size="small"
                text
                disabled={busy}
                onClick={() => apply({ category: "owner_alerts", clearSnooze: true })}
              />
            )}
          </div>
        ) : (
          <span className="text-color-secondary text-sm">Not snoozed.</span>
        )}
        {canEdit && (
          <div className="flex align-items-end gap-2">
            <Calendar
              inputId="snooze-until"
              value={snoozeAt}
              onChange={(e) => setSnoozeAt((e.value as Date) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
            />
            <Button
              label="Snooze until"
              size="small"
              disabled={busy || !snoozeAt}
              onClick={() =>
                snoozeAt &&
                apply({ category: "owner_alerts", snoozedUntil: snoozeAt.toISOString() })
              }
            />
          </div>
        )}
      </div>

      {writeError && (
        <div data-testid="preferences-write-error">
          <Message severity="error" text={`Could not save: ${writeError}`} />
        </div>
      )}
    </div>
  );
};

export default NotificationPreferencesPanel;
